import { ReactElement } from 'react';
import { useMediaQuery } from 'react-responsive';
import { DialogButton, Typography } from '@guildeducationinc/recess';
import { ModalOptions, useModal } from '@src/context/ModalContext';
import { Application } from '@src/hooks/useApplications';
import { formatCommaSeparated } from '@src/utilities/format';
import { getAcademicPartnerName } from '@src/utilities/applicationUtility';
import { APPLICATIONS_URL, MOBILE_QUERY } from '@src/constants';
import { clickable } from '@src/analytics';
import useMount from '@src/hooks/useMount';
import '../EnrollmentConfirmationModal/EnrollmentConfirmationModal.scss';

export interface MultipleEnrollmentModalProps {
  applications: Application[];
}

const MultipleEnrollmentConfirmationModal = ({
  applications,
}: MultipleEnrollmentModalProps): ReactElement => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { closeModal, setModalOptions } = useModal();

  const academicPartners = applications.map(getAcademicPartnerName);
  const bodyText = `Take a moment to confirm enrollment for the next ${formatCommaSeparated(
    academicPartners
  )} start dates.`;

  const handlePrimaryButton = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'home_multiple_enrollment_modal',
      text: 'view_next_steps',
      type: 'button',
    });
  };

  const handleSecondaryButton = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'home_multiple_enrollment_modal',
      text: 'not_now',
      type: 'button',
    });
    closeModal();
  };

  const primaryButtonConfig: DialogButton = {
    onClick: handlePrimaryButton,
    href: APPLICATIONS_URL,
    text: 'View next steps',
  };

  const secondaryButtonConfig: DialogButton = {
    appearance: 'outline',
    onClick: handleSecondaryButton,
    text: 'Not now',
  };

  const modalOptions: ModalOptions = {
    primaryButton: primaryButtonConfig,
    secondaryButton: secondaryButtonConfig,
    closable: true,
    describedBy: 'enrollment-modal-description',
    labelledBy: 'enrollment-modal-title',
    tag: 'enrollment_modal',
  };

  useMount(() => {
    setModalOptions(modalOptions);
  });

  return (
    <div className='enrollmentModal'>
      <div className='enrollmentModalHeader'>
        <Typography
          id='enrollment-modal-title'
          variant='h3'
          gutterBottom={isMobile ? 'md' : undefined}
        >
          It&rsquo;s time to make a decision about your next step.
        </Typography>
      </div>
      <div>
        <Typography variant='body1' gutterBottom='sm'>
          {bodyText}
        </Typography>
      </div>
    </div>
  );
};

export default MultipleEnrollmentConfirmationModal;
